import firebase from "datastore/database";
import store from "datastore/store";

import fetchDoc from "util/api/fetchDoc";
import { fbTsToDate, newfbTsToDate } from "common/date";
import fetchCustomerLocationData from "util/fetchCustomerLocationData";

export { default as get } from "./get";

const getCollectionUrl = (uid) => `marketing-campaigns/business/${uid}`;

export async function update(businessUid, uid, data) {
  return firebase
    .firestore()
    .collection(getCollectionUrl(businessUid))
    .doc(uid)
    .set(data, { merge: true });
}

export function add(businessUid, data) {
  const doc = firebase
    .firestore()
    .collection(getCollectionUrl(businessUid))
    .doc();
  doc.set(data, { merge: true });

  return { uid: doc.id, ...data };
}

export async function deleteCampaign(businessUid, uid) {
  return firebase
    .firestore()
    .collection(getCollectionUrl(businessUid))
    .doc(uid)
    .delete();
}

export const getCampaign = async (businessUid, uid) =>
  fetchDoc(`marketing-campaigns/business/${businessUid}/${uid}`);

const addClick = async (clicksPath, customerUid, customerLocationData) => {
  const data = {
    customerUid,
    ...customerLocationData,
    createdAt: new Date(),
  };
  const doc = await firebase
    .firestore()
    .collection(clicksPath)
    .doc();
  doc.set(data);
  return { uid: doc.id, ...data };
};

const toDayClicks = (clicks) => {
  const today = new Date();
  const todayClicks = clicks.filter(
    (click) =>
      click.createdAt.getDate() === today.getDate() &&
      click.createdAt.getMonth() === today.getMonth() &&
      click.createdAt.getFullYear() === today.getFullYear()
  );
  return todayClicks;
};

const getCampaignClicks = async (customerUid, clicksPath) => {
  const snap = await firebase
    .firestore()
    .collection(clicksPath)
    .where("customerUid", "==", customerUid)
    .get();
  if (snap.empty || snap.docs.length === 0) return [];
  let clicks = snap.docs.map((doc) => ({
    uid: doc.id,
    ...doc.data(),
  }));
  clicks = clicks.map((click) => {
    return { ...click, createdAt: fbTsToDate(click.createdAt) };
  });
  const todayClicks = toDayClicks(clicks);
  if (todayClicks.length > 0)
    return {
      alreadyClicked: true,
    };
  return {
    alreadyClicked: false,
  };
};

const updateCampaignClicks = async (campaignPath) => {
  const doc = firebase.firestore().doc(campaignPath);
  return await doc.set(
    { clicks: firebase.firestore.FieldValue.increment(1) },
    { merge: true }
  );
};

const getRecipitient = async (recipientsPath, customerUid, channel) => {
  const snap = await firebase
    .firestore()
    .collection(recipientsPath)
    .where("customerUid", "==", customerUid)
    .get();
  if (snap.empty || snap.docs.length === 0) return [];
  let recipitients = snap.docs.map((doc) => ({
    uid: doc.id,
    ...doc.data(),
  }));
  //sort by campaignTime
  const data = recipitients.sort(
    (a, b) => newfbTsToDate(b.campaignTime) - newfbTsToDate(a.campaignTime)
  );
  const recipientDoc = data.find((doc) => doc.channel === channel);
  // return data[0].uid;
  // return data?.length ? data[0] : null;
  return recipientDoc;
};

export const updateCampaingClicks = async (businessUid, queryString) => {
  const {
    campUid: campaignUid,
    cuid: customerUid,
    type: campaignType,
  } = queryString;

  const collection =
    campaignType === "auto" ? "marketing-automations" : "marketing-campaigns";
  const campaignPath = `${collection}/business/${businessUid}/${campaignUid}`;
  const clicksPath = `${campaignPath}/clicks`;
  let response = await getCampaignClicks(customerUid, clicksPath);
  if (response.alreadyClicked) return;
  const customerLocationData = await fetchCustomerLocationData(
    businessUid,
    customerUid
  );
  await updateCampaignClicks(campaignPath);
  return await addClick(clicksPath, customerUid, customerLocationData);
};

export const updateRecipientStatus = async (businessUid, queryString) => {
  const {
    campUid: campaignUid,
    cuid: customerUid,
    type: campaignType,
    channel,
  } = queryString;
  const collection =
    campaignType === "auto" ? "marketing-automations" : "marketing-campaigns";
  const campaignPath = `${collection}/business/${businessUid}/${campaignUid}`;
  const recipientsPath = `${campaignPath}/recipients`;
  //get recipient by customerUid
  const { uid: recipientUid, status } =
    (await getRecipitient(recipientsPath, customerUid, channel)) ?? {};
  if (!recipientUid || status !== "delivered") return null;
  const doc = firebase
    .firestore()
    .collection(recipientsPath)
    .doc(recipientUid);
  return await doc.set({ status: "opened" }, { merge: true });
};

export const observe = (businessUid) => {
  store.dispatch({ type: "ADD_MARKETING_CAMPAIGNS_LOADING" });
  return firebase
    .firestore()
    .collection(getCollectionUrl(businessUid))
    .onSnapshot((snapshot) => {
      store.dispatch({ type: "SET_MARKETING_CAMPAIGNS_LOADING", data: false });
      const updates = {
        added: [],
        modified: [],
        removed: [],
      };

      snapshot.docChanges().forEach((change) => {
        if (change.type === "added") {
          updates.added.push({ uid: change.doc.id, ...change.doc.data() });
        }
        if (change.type === "modified") {
          updates.modified.push({ uid: change.doc.id, ...change.doc.data() });
        }
        if (change.type === "removed") {
          updates.removed.push({ uid: change.doc.id, ...change.doc.data() });
        }
      });
      if (updates.added.length > 0)
        store.dispatch({
          type: "ADD_MARKETING_CAMPAIGNS",
          data: updates.added,
        });
      if (updates.modified.length > 0)
        store.dispatch({
          type: "UPDATE_MARKETING_CAMPAIGNS",
          data: updates.modified,
        });
      if (updates.removed.length > 0)
        store.dispatch({
          type: "DELETE_MARKETING_CAMPAIGNS",
          data: updates.removed,
        });
    });
};
